import { useTranslation } from 'react-i18next'

import { useTheme } from 'styled-components'

import { Checkbox, ComboBox, InputWithLabel, Text } from '@reveni-io/reveni-ui'

import { initCriterion } from '@/common/components/ConditionsEditor/constants'
import CurrencySelector from '@/common/components/CurrencySelector/CurrencySelector'
import { getCurrencySymbolByCode } from '@/common/helpers/helpers'
import AffectedProductsEditor from '@/configuration/pages/ConfigurationPage/ReturnPolicy/PolicyRulesConfiguration/PolicyRuleDetailConfiguration/PolicyRuleActionEditor/AffectedProductsEditor/AffectedProductsEditor'

import { bonusAppliesToOptions, bonusAppliesToValues, discountTypeOptions, discountTypeValues } from './constants'
import { getErrors } from './helpers'
import * as Styles from './styles'

const ApplyBonusConfig = ({
  config,
  onChangeConfig,
  disabled,
  currencies,
  storeCurrencyCode,
  productTypes,
  productCategories,
  productCollections,
  errors = [],
}) => {
  const theme = useTheme()
  const { t } = useTranslation()
  const isPercentage = config.bonus_exchange_discount_type === discountTypeValues.percentage
  const { hasError, hasDiscountTypeError, hasDiscountLabelError, hasDiscountValueError, hasProductConditionError } =
    getErrors(errors)
  const translatedDiscountTypeOptions = discountTypeOptions.map(option => ({
    value: option.value,
    label: t(option.labelKey),
  }))
  const translatedAppliesToOptions = bonusAppliesToOptions.map(option => ({
    value: option.value,
    label: t(option.labelKey),
  }))
  const showProductSelector = config.bonus_exchange_discount_applies_to === bonusAppliesToValues.newRequestedProducts
  const selectedCurrency = config.bonus_exchange_discount_currency || storeCurrencyCode

  const handleDiscountToggle = e => {
    const checked = e.target.checked
    if (checked) {
      onChangeConfig({ bonus_exchange_discount: true })
    } else {
      onChangeConfig({
        bonus_exchange_discount: false,
        bonus_exchange_discount_type: '',
        bonus_exchange_discount_value: '',
        bonus_exchange_discount_currency: null,
        bonus_exchange_discount_label: '',
        bonus_exchange_discount_applies_to: bonusAppliesToValues.exchangeOrder,
        product_criteria: [initCriterion],
      })
    }
  }

  const handleDiscountTypeChange = e => {
    const value = e.target.value

    if (value === discountTypeValues.percentage) {
      onChangeConfig({ bonus_exchange_discount_type: value, bonus_exchange_discount_currency: null })
      return
    }

    onChangeConfig({
      bonus_exchange_discount_type: value,
      bonus_exchange_discount_currency: config.bonus_exchange_discount_currency || storeCurrencyCode,
    })
  }

  const handleDiscountCurrencyChange = value => onChangeConfig({ bonus_exchange_discount_currency: value })

  const handleDiscountValueChange = e => onChangeConfig({ bonus_exchange_discount_value: e.target.value })

  const handleAppliesToChange = e => {
    const value = e.target.value
    if (value === bonusAppliesToValues.exchangeOrder) {
      onChangeConfig({
        bonus_exchange_discount_applies_to: value,
        product_criteria: [initCriterion],
      })
    } else {
      onChangeConfig({ bonus_exchange_discount_applies_to: value })
    }
  }

  return (
    <Styles.Container>
      <Text type="bodyMBold">{t('Beneficio')}</Text>
      <Styles.CheckboxesContainer>
        <Checkbox
          label={t('Devolución gratuita')}
          checked={config.bonus_free_return || false}
          onChange={e => onChangeConfig({ bonus_free_return: e.target.checked })}
          disabled={disabled}
          error={hasError}
        />
        <Checkbox
          label={t('Cambio gratuito')}
          checked={config.bonus_free_exchange || false}
          onChange={e => onChangeConfig({ bonus_free_exchange: e.target.checked })}
          disabled={disabled}
          error={hasError}
        />
        <Checkbox
          label={t('Reembolsar gastos de envío del pedido original')}
          checked={config.bonus_refund_shipping || false}
          onChange={e => onChangeConfig({ bonus_refund_shipping: e.target.checked })}
          disabled={disabled}
          error={hasError}
        />
        <Checkbox
          label={t('Descuento')}
          checked={config.bonus_exchange_discount || false}
          onChange={handleDiscountToggle}
          disabled={disabled}
          error={hasError}
        />
      </Styles.CheckboxesContainer>
      {config.bonus_exchange_discount && (
        <Styles.DiscountFieldsContainer>
          <Text type="bodyMBold">{t('Configuración del descuento')}</Text>
          <InputWithLabel
            label={t('Nombre del descuento')}
            value={config.bonus_exchange_discount_label || ''}
            onChange={e => onChangeConfig({ bonus_exchange_discount_label: e.target.value })}
            maxLength={50}
            showCounter
            fullWidth
            disabled={disabled}
            backgroundColor={theme.colors.background.primary}
            error={hasDiscountLabelError}
            helperText={hasDiscountLabelError ? t('Este campo es obligatorio') : undefined}
          />
          <ComboBox
            label={t('Aplica a')}
            menuItems={translatedAppliesToOptions}
            handleChange={handleAppliesToChange}
            value={config.bonus_exchange_discount_applies_to || bonusAppliesToValues.exchangeOrder}
            fullWidth
            disabled={disabled}
            backgroundColor={theme.colors.background.primary}
          />
          <ComboBox
            label={t('Tipo de descuento')}
            menuItems={translatedDiscountTypeOptions}
            handleChange={handleDiscountTypeChange}
            value={config.bonus_exchange_discount_type || ''}
            fullWidth
            disabled={disabled}
            backgroundColor={theme.colors.background.primary}
            error={hasDiscountTypeError}
            helperText={hasDiscountTypeError ? t('Este campo es obligatorio') : ' '}
          />
          {isPercentage ? (
            <InputWithLabel
              label={t('Porcentaje')}
              value={config.bonus_exchange_discount_value || ''}
              onChange={handleDiscountValueChange}
              type="number"
              fullWidth
              disabled={disabled}
              startAdornment="%"
              backgroundColor={theme.colors.background.primary}
              error={hasDiscountValueError}
              helperText={hasDiscountValueError ? t('Este campo es obligatorio') : undefined}
            />
          ) : (
            <Styles.AmountRow>
              <CurrencySelector
                currencies={currencies}
                selectedCurrency={selectedCurrency}
                onChange={handleDiscountCurrencyChange}
                disabled={disabled}
                backgroundColor={theme.colors.background.primary}
              />
              <InputWithLabel
                label={t('Importe')}
                value={config.bonus_exchange_discount_value || ''}
                onChange={handleDiscountValueChange}
                type="number"
                fullWidth
                disabled={disabled}
                startAdornment={getCurrencySymbolByCode(selectedCurrency, currencies)}
                backgroundColor={theme.colors.background.primary}
                error={hasDiscountValueError}
                helperText={hasDiscountValueError ? t('Este campo es obligatorio') : undefined}
              />
            </Styles.AmountRow>
          )}
          {showProductSelector && (
            <AffectedProductsEditor
              config={config}
              onChangeConfig={onChangeConfig}
              disabled={disabled}
              currencies={currencies}
              storeCurrencyCode={storeCurrencyCode}
              productTypes={productTypes}
              productCategories={productCategories}
              productCollections={productCollections}
              hasError={hasProductConditionError}
            />
          )}
        </Styles.DiscountFieldsContainer>
      )}
    </Styles.Container>
  )
}

export default ApplyBonusConfig
